import {
  buildFetchElement,
  renderFetchXml,
  type FetchXmlBuilderTree,
  type IFetchXmlBuilderDataSource,
  type JoinDraft,
  type QueryEntityMetadata,
  type QueryHierarchyExecutionOptions,
} from "@kodikas/fetchxml-builder";
import {
  composeHierarchicalRows,
  createRelationBatchFetch,
  planHierarchicalFetch,
  splitInBatches,
  toFetchXml,
  type FetchElement,
  type HierarchicalRelationCardinality,
  type HierarchicalRelationHint,
} from "@kodikas/fetchxml-ts";
import { DataverseClient } from "./client";
import { loadEntityDefinitions, loadEntityMetadata } from "./metadata";
import type { DataverseConnectionConfig, DataverseEntityDefinitionRecord } from "./types";

interface DataverseExecutionResponse {
  rows: Array<Record<string, unknown>>;
  raw: unknown;
  requestUrl: string;
  status: number;
  statusText: string;
  pageCount: number;
  truncated: boolean;
}

interface ODataFetchXmlPage {
  value?: unknown[];
  "@odata.nextLink"?: string;
}

const FETCHXML_SAFE_QUERY_LENGTH = 7000;
const DEFAULT_RELATION_PAGE_LIMIT = 25;
const DEFAULT_RELATION_BATCH_CONCURRENCY = 3;

function findRootEntitySetName(entities: DataverseEntityDefinitionRecord[], rootEntityLogicalName: string): string | undefined {
  return entities.find((item) => item.logicalName === rootEntityLogicalName)?.entitySetName;
}

function findEntityDefinition(
  entities: DataverseEntityDefinitionRecord[],
  logicalName: string,
): DataverseEntityDefinitionRecord | undefined {
  return entities.find((item) => item.logicalName === logicalName);
}

function resolveRelationCardinality(
  join: JoinDraft,
  parentPrimaryKeyAttribute?: string,
): HierarchicalRelationCardinality {
  if (join.relationKey.startsWith("o2m:")) {
    return "one-to-many";
  }

  if (join.relationKey.startsWith("m2m:")) {
    return "many-to-many";
  }

  if (parentPrimaryKeyAttribute && join.toAttribute === parentPrimaryKeyAttribute) {
    return "one-to-many";
  }

  return "many-to-one";
}

function getJoinById(joins: JoinDraft[]): Map<string, JoinDraft> {
  return new Map(joins.map((join) => [join.id, join]));
}

function resolveParentLogicalName(
  tree: FetchXmlBuilderTree,
  joinById: Map<string, JoinDraft>,
  join: JoinDraft,
): string | null {
  if (join.parentTarget === "root") {
    return tree.rootEntityLogicalName;
  }

  return joinById.get(join.parentTarget)?.targetEntityLogicalName || null;
}

function buildRelationHints(
  tree: FetchXmlBuilderTree,
  entities: DataverseEntityDefinitionRecord[],
  metadataByLogicalName: Map<string, QueryEntityMetadata>,
): HierarchicalRelationHint[] {
  const aliasByJoinId = new Map<string, string>();
  const joinById = getJoinById(tree.joins);

  tree.joins.forEach((join) => {
    aliasByJoinId.set(join.id, join.alias.trim());
  });

  return tree.joins
    .filter((join) => join.alias.trim().length > 0)
    .map((join) => {
      const parentLogicalName = resolveParentLogicalName(tree, joinById, join);
      const parentPrimaryKeyAttribute = parentLogicalName
        ? findEntityDefinition(entities, parentLogicalName)?.primaryIdAttribute
        : undefined;
      const relationDefinition = parentLogicalName
        ? metadataByLogicalName.get(parentLogicalName)?.relations.find((relation) => relation.key === join.relationKey)
        : undefined;
      const peerPrimaryIdAttribute = relationDefinition?.manyToMany
        ? findEntityDefinition(entities, relationDefinition.manyToMany.peerEntityLogicalName)?.primaryIdAttribute
        : undefined;

      return {
        alias: join.alias.trim(),
        parentAlias: join.parentTarget === "root" ? "root" : aliasByJoinId.get(join.parentTarget) || "root",
        cardinality:
          relationDefinition?.relationType || resolveRelationCardinality(join, parentPrimaryKeyAttribute),
        targetEntityLogicalName: join.targetEntityLogicalName,
        fromAttribute: join.fromAttribute,
        toAttribute: join.toAttribute,
        manyToMany: relationDefinition?.manyToMany
          ? {
              ...relationDefinition.manyToMany,
              peerPrimaryIdAttribute,
            }
          : undefined,
      };
    })
    .filter((relation) => relation.targetEntityLogicalName && relation.fromAttribute && relation.toAttribute);
}

function ensureRootPrimaryKey(tree: FetchXmlBuilderTree, rootPrimaryKeyAttribute: string): FetchXmlBuilderTree {
  if (tree.rootSelectedAttributes.includes(rootPrimaryKeyAttribute)) {
    return tree;
  }

  return {
    ...tree,
    rootSelectedAttributes: [rootPrimaryKeyAttribute, ...tree.rootSelectedAttributes],
  };
}

function buildAliasSeed(targetEntityLogicalName: string): string {
  const parts = targetEntityLogicalName
    .split("_")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const candidate =
    parts
      .map((part) => part[0].toUpperCase())
      .join("")
      .slice(0, 4) || targetEntityLogicalName.slice(0, 3).toUpperCase();

  return candidate || "J";
}

function ensureJoinAliases(tree: FetchXmlBuilderTree): FetchXmlBuilderTree {
  const usedAliases = new Set<string>();

  const joins = tree.joins.map((join) => {
    const existing = join.alias.trim();
    if (existing && !usedAliases.has(existing)) {
      usedAliases.add(existing);
      return join;
    }

    const base = buildAliasSeed(join.targetEntityLogicalName || "join");
    let nextAlias = base;
    let suffix = 2;

    while (usedAliases.has(nextAlias)) {
      nextAlias = `${base}${suffix}`;
      suffix += 1;
    }

    usedAliases.add(nextAlias);
    return {
      ...join,
      alias: nextAlias,
    };
  });

  return {
    ...tree,
    joins,
  };
}

function getUniqueParentIds(rows: Array<Record<string, unknown>>, attributeName: string): Array<string | number | boolean> {
  const values = new Set<string>();
  const result: Array<string | number | boolean> = [];

  rows.forEach((row) => {
    const value = row[attributeName];
    if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") {
      return;
    }

    const lookup = String(value);
    if (values.has(lookup)) {
      return;
    }

    values.add(lookup);
    result.push(value);
  });

  return result;
}

function getAliasParentRowsFromRoot(
  rootRows: Array<Record<string, unknown>>,
  parentAlias: string,
  attributeName: string,
): Array<Record<string, unknown>> {
  const key = `${parentAlias}.${attributeName}`;
  return rootRows
    .filter((row) => row[key] !== undefined && row[key] !== null)
    .map((row) => ({ [attributeName]: row[key] }));
}

async function executeFetchElement(
  client: DataverseClient,
  entitySetName: string,
  fetchElement: FetchElement,
  pageLimit: number,
): Promise<DataverseExecutionResponse> {
  const encoded = encodeURIComponent(toFetchXml(fetchElement));
  const requestPath = `/${entitySetName}?fetchXml=${encoded}`;
  const rows: Array<Record<string, unknown>> = [];
  const rawPages: unknown[] = [];
  let pageCount = 0;
  let requestUrl = "";
  let status = 200;
  let statusText = "";
  let nextRequestUrl: string | null = client.resolveUrl(requestPath);

  while (nextRequestUrl && pageCount < pageLimit) {
    const response: {
      data: ODataFetchXmlPage;
      status: number;
      statusText: string;
      requestUrl: string;
    } = await client.fetchJsonUrlWithDiagnostics<ODataFetchXmlPage>(nextRequestUrl);
    requestUrl = response.requestUrl;
    status = response.status;
    statusText = response.statusText;
    pageCount += 1;
    rawPages.push(response.data);

    const pageRows = Array.isArray(response.data.value)
      ? response.data.value.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"))
      : [];
    rows.push(...pageRows);

    const nextLink = typeof response.data["@odata.nextLink"] === "string" ? response.data["@odata.nextLink"] : null;
    nextRequestUrl = nextLink ? (nextLink.startsWith("http") ? nextLink : client.resolveUrl(nextLink)) : null;
  }

  const truncated = Boolean(nextRequestUrl);

  return {
    rows,
    raw: rawPages.length === 1 ? rawPages[0] : rawPages,
    requestUrl,
    status,
    statusText,
    pageCount,
    truncated,
  };
}

function splitOversizedBatches(
  relationPlan: ReturnType<typeof planHierarchicalFetch>["relationPlans"][number],
  parentIds: Array<string | number | boolean>,
  batchSize: number,
): Array<Array<string | number | boolean>> {
  const pending = splitInBatches(parentIds, batchSize);
  const safeBatches: Array<Array<string | number | boolean>> = [];

  while (pending.length > 0) {
    const batch = pending.shift();
    if (!batch || batch.length < 1) {
      continue;
    }

    const encodedLength = encodeURIComponent(toFetchXml(createRelationBatchFetch(relationPlan, batch))).length;
    if (encodedLength <= FETCHXML_SAFE_QUERY_LENGTH || batch.length === 1) {
      safeBatches.push(batch);
      continue;
    }

    const midpoint = Math.ceil(batch.length / 2);
    pending.unshift(batch.slice(midpoint));
    pending.unshift(batch.slice(0, midpoint));
  }

  return safeBatches;
}

function buildMinimalEntityFetch(
  entityLogicalName: string,
  requiredAttributes: string[],
): FetchElement {
  const uniqueAttributes = Array.from(new Set(requiredAttributes.filter((attribute) => attribute.trim().length > 0)));
  return {
    name: "fetch",
    attributes: {
      version: "1.0",
      mapping: "logical",
    },
    children: [
      {
        name: "entity",
        attributes: { name: entityLogicalName },
        children: uniqueAttributes.map((attributeName) => ({
          name: "attribute",
          attributes: { name: attributeName },
          children: [],
        })),
      },
    ],
  };
}

interface RelationBatchResult {
  rows: Array<Record<string, unknown>>;
  raw: unknown;
  pageCount: number;
  truncated: boolean;
}

async function executeManyToManyBatch(
  client: DataverseClient,
  relationPlan: ReturnType<typeof planHierarchicalFetch>["relationPlans"][number],
  batch: Array<string | number | boolean>,
  relationPageLimit: number,
): Promise<RelationBatchResult> {
  const m2m = relationPlan.manyToMany;
  if (!m2m || !m2m.peerPrimaryIdAttribute) {
    return {
      rows: [],
      raw: { skipped: "many-to-many metadata incomplete" },
      pageCount: 0,
      truncated: false,
    };
  }

  const intersectFetch = buildMinimalEntityFetch(m2m.intersectEntityLogicalName, [
    m2m.localIntersectAttribute,
    m2m.peerIntersectAttribute,
  ]);
  const intersectBatchFetch = createRelationBatchFetch(
    {
      ...relationPlan,
      fetchElement: intersectFetch,
      fromAttribute: m2m.localIntersectAttribute,
    },
    batch,
  );
  const intersectExecution = await executeFetchElement(
    client,
    m2m.intersectEntityLogicalName,
    intersectBatchFetch,
    relationPageLimit,
  );

  const parentKeysByPeerKey = new Map<string, string[]>();
  const peerIds: Array<string | number | boolean> = [];
  const seenPeerIds = new Set<string>();

  intersectExecution.rows.forEach((row) => {
    const parentKey = row[m2m.localIntersectAttribute];
    const peerKey = row[m2m.peerIntersectAttribute];
    if (
      (typeof parentKey !== "string" && typeof parentKey !== "number" && typeof parentKey !== "boolean") ||
      (typeof peerKey !== "string" && typeof peerKey !== "number" && typeof peerKey !== "boolean")
    ) {
      return;
    }

    const normalizedPeerKey = String(peerKey);
    if (!parentKeysByPeerKey.has(normalizedPeerKey)) {
      parentKeysByPeerKey.set(normalizedPeerKey, []);
    }
    parentKeysByPeerKey.get(normalizedPeerKey)?.push(String(parentKey));

    if (!seenPeerIds.has(normalizedPeerKey)) {
      seenPeerIds.add(normalizedPeerKey);
      peerIds.push(peerKey);
    }
  });

  if (peerIds.length < 1) {
    return {
      rows: [],
      raw: {
        intersect: intersectExecution.raw,
        peer: [],
      },
      pageCount: intersectExecution.pageCount,
      truncated: intersectExecution.truncated,
    };
  }

  const peerFetch = buildMinimalEntityFetch(m2m.peerEntityLogicalName, [m2m.peerPrimaryIdAttribute]);
  const peerBatchFetch = createRelationBatchFetch(
    {
      ...relationPlan,
      fetchElement: peerFetch,
      fromAttribute: m2m.peerPrimaryIdAttribute,
    },
    peerIds,
  );
  const peerExecution = await executeFetchElement(client, m2m.peerEntityLogicalName, peerBatchFetch, relationPageLimit);

  const rows: Array<Record<string, unknown>> = [];
  peerExecution.rows.forEach((peerRow) => {
    const peerValue = peerRow[m2m.peerPrimaryIdAttribute as string];
    if (typeof peerValue !== "string" && typeof peerValue !== "number" && typeof peerValue !== "boolean") {
      return;
    }

    const parentKeys = parentKeysByPeerKey.get(String(peerValue)) || [];
    parentKeys.forEach((parentKey) => {
      rows.push({
        ...peerRow,
        [relationPlan.fromAttribute]: parentKey,
      });
    });
  });

  return {
    rows,
    raw: {
      intersect: intersectExecution.raw,
      peer: peerExecution.raw,
    },
    pageCount: intersectExecution.pageCount + peerExecution.pageCount,
    truncated: intersectExecution.truncated || peerExecution.truncated,
  };
}

async function executeBatchesWithConcurrency<TInput, TOutput>(
  batches: ReadonlyArray<TInput>,
  concurrency: number,
  execute: (batch: TInput) => Promise<TOutput>,
): Promise<TOutput[]> {
  if (batches.length < 1) {
    return [];
  }

  const normalizedConcurrency = Math.max(1, Math.min(concurrency, batches.length));
  const results: TOutput[] = new Array(batches.length);
  let cursor = 0;

  const worker = async (): Promise<void> => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= batches.length) {
        return;
      }

      results[index] = await execute(batches[index]);
    }
  };

  await Promise.all(Array.from({ length: normalizedConcurrency }, () => worker()));
  return results;
}

export function createDataverseDataSource(connection: DataverseConnectionConfig): IFetchXmlBuilderDataSource {
  const client = new DataverseClient(connection);
  let entitiesCache: DataverseEntityDefinitionRecord[] | null = null;

  const listEntities = async (): Promise<DataverseEntityDefinitionRecord[]> => {
    if (!entitiesCache) {
      entitiesCache = await loadEntityDefinitions(client);
    }

    return entitiesCache;
  };

  const metadataCache = new Map<string, QueryEntityMetadata>();
  const getEntityMetadataCached = async (logicalName: string): Promise<QueryEntityMetadata> => {
    const key = logicalName.trim();
    if (!metadataCache.has(key)) {
      metadataCache.set(key, await loadEntityMetadata(client, key));
    }

    return metadataCache.get(key) as QueryEntityMetadata;
  };

  return {
    getConnectionLabel: () => client.normalizedBaseUrl,
    listEntities,
    getEntityMetadata: (logicalName: string) => getEntityMetadataCached(logicalName),
    executeQuery: async (tree: FetchXmlBuilderTree) => {
      const entities = await listEntities();
      const entitySetName = findRootEntitySetName(entities, tree.rootEntityLogicalName);
      if (!entitySetName) {
        throw new Error(`Unable to find entity set name for '${tree.rootEntityLogicalName}'.`);
      }

      const fetchXml = renderFetchXml(tree);
      const encoded = encodeURIComponent(fetchXml);
      const requestPath = `/${entitySetName}?fetchXml=${encoded}`;
      const startedAt = performance.now();

      const response = await client.fetchJsonWithDiagnostics<{ value?: unknown[] }>(requestPath);
      const rows = Array.isArray(response.data.value) ? response.data.value : [];

      return {
        rows,
        raw: response.data,
        durationMs: Math.round(performance.now() - startedAt),
        requestUrl: response.requestUrl,
        status: response.status,
        statusText: response.statusText,
        targetName: entitySetName,
      };
    },
    executeQueryHierarchical: async (tree: FetchXmlBuilderTree, options?: QueryHierarchyExecutionOptions) => {
      const startedAt = performance.now();
      const entities = await listEntities();
      const rootEntity = findEntityDefinition(entities, tree.rootEntityLogicalName);
      if (!rootEntity) {
        throw new Error(`Unable to resolve root entity '${tree.rootEntityLogicalName}'.`);
      }

      const rootPrimaryKeyAttribute = rootEntity.primaryIdAttribute || `${tree.rootEntityLogicalName}id`;
      const executionTree = ensureJoinAliases(ensureRootPrimaryKey(tree, rootPrimaryKeyAttribute));
      const parentLogicalNames = new Set<string>();
      parentLogicalNames.add(executionTree.rootEntityLogicalName);
      const joinById = getJoinById(executionTree.joins);
      executionTree.joins.forEach((join) => {
        const parentLogicalName = resolveParentLogicalName(executionTree, joinById, join);
        if (parentLogicalName) {
          parentLogicalNames.add(parentLogicalName);
        }
      });
      const metadataByLogicalName = new Map<string, QueryEntityMetadata>();
      for (const logicalName of parentLogicalNames) {
        metadataByLogicalName.set(logicalName, await getEntityMetadataCached(logicalName));
      }

      const relationHints = buildRelationHints(executionTree, entities, metadataByLogicalName);
      const rootPageLimit = Math.max(1, options?.rootPageLimit ?? 1);
      const relationPageLimit = Math.max(1, options?.relationPageLimit ?? DEFAULT_RELATION_PAGE_LIMIT);
      const relationBatchConcurrency = Math.max(1, options?.relationBatchConcurrency ?? DEFAULT_RELATION_BATCH_CONCURRENCY);
      const plan = planHierarchicalFetch(buildFetchElement(executionTree), relationHints, {
        rootPrimaryKeyAttribute,
        rootBatchSize: options?.rootBatchSize ?? 500,
        inBatchSize: options?.inBatchSize ?? 1000,
      });

      const rootExecution = await executeFetchElement(client, rootEntity.entitySetName, plan.rootFetchElement, rootPageLimit);
      const rootRows = rootExecution.rows;
      const relationRowsByAlias: Record<string, Array<Record<string, unknown>>> = {};
      const relationBatchCountByAlias: Record<string, number> = {};
      const relationPageCountByAlias: Record<string, number> = {};
      const relationRowCountByAlias: Record<string, number> = {};
      const relationTruncatedByAlias: Record<string, boolean> = {};
      const relationRawByAlias: Record<string, unknown> = {};

      for (const relationPlan of plan.relationPlans) {
        const parentRows =
          relationPlan.parentAlias === "root"
            ? rootRows
            : relationRowsByAlias[relationPlan.parentAlias] ||
              getAliasParentRowsFromRoot(rootRows, relationPlan.parentAlias, relationPlan.toAttribute);
        const parentIds = getUniqueParentIds(parentRows, relationPlan.toAttribute);

        if (parentIds.length < 1) {
          relationRowsByAlias[relationPlan.alias] = [];
          relationBatchCountByAlias[relationPlan.alias] = 0;
          relationPageCountByAlias[relationPlan.alias] = 0;
          relationRowCountByAlias[relationPlan.alias] = 0;
          relationTruncatedByAlias[relationPlan.alias] = false;
          relationRawByAlias[relationPlan.alias] = [];
          continue;
        }

        const relationEntity = findEntityDefinition(entities, relationPlan.targetEntityLogicalName);
        if (!relationEntity) {
          relationRowsByAlias[relationPlan.alias] = [];
          relationBatchCountByAlias[relationPlan.alias] = 0;
          relationPageCountByAlias[relationPlan.alias] = 0;
          relationRowCountByAlias[relationPlan.alias] = 0;
          relationTruncatedByAlias[relationPlan.alias] = false;
          relationRawByAlias[relationPlan.alias] = {
            error: `Unable to resolve entity set for '${relationPlan.targetEntityLogicalName}'.`,
          };
          continue;
        }

        const batches = splitOversizedBatches(relationPlan, parentIds, plan.inBatchSize);
        relationBatchCountByAlias[relationPlan.alias] = batches.length;

        const batchExecutions = await executeBatchesWithConcurrency(
          batches,
          relationBatchConcurrency,
          async (batch) => {
            if (batch.length < 1) {
              return {
                rows: [] as Array<Record<string, unknown>>,
                raw: [],
                requestUrl: "",
                status: 200,
                statusText: "",
                pageCount: 0,
                truncated: false,
              };
            }

            if (relationPlan.cardinality === "many-to-many" && relationPlan.parentAlias === "root") {
              return executeManyToManyBatch(client, relationPlan, batch, relationPageLimit);
            }

            const fetchForBatch = createRelationBatchFetch(relationPlan, batch);
            return executeFetchElement(client, relationEntity.entitySetName, fetchForBatch, relationPageLimit);
          },
        );

        const aliasRows: Array<Record<string, unknown>> = [];
        const rawParts: unknown[] = [];
        let aliasPageCount = 0;
        let aliasTruncated = false;

        batchExecutions.forEach((relationExecution) => {
          aliasRows.push(...relationExecution.rows);
          aliasPageCount += relationExecution.pageCount;
          aliasTruncated = aliasTruncated || relationExecution.truncated;
          rawParts.push(relationExecution.raw);
        });

        relationRowsByAlias[relationPlan.alias] = aliasRows;
        relationPageCountByAlias[relationPlan.alias] = aliasPageCount;
        relationRowCountByAlias[relationPlan.alias] = aliasRows.length;
        relationTruncatedByAlias[relationPlan.alias] = aliasTruncated;
        relationRawByAlias[relationPlan.alias] = rawParts;
      }

      const hierarchicalRows = composeHierarchicalRows({
        rootRows,
        relationRowsByAlias,
        relationHints,
        rootPrimaryKeyAttribute: plan.rootPrimaryKeyAttribute,
      });

      return {
        rows: rootRows,
        hierarchicalRows,
        raw: {
          root: rootExecution.raw,
          relations: relationRawByAlias,
        },
        hierarchyDiagnostics: {
          mode: "hierarchical",
          rootBatchSize: plan.rootBatchSize,
          rootPageLimit,
          rootPageCount: rootExecution.pageCount,
          rootTruncated: rootExecution.truncated,
          inBatchSize: plan.inBatchSize,
          relationPageLimit,
          relationBatchConcurrency,
          rootRowCount: rootRows.length,
          relationBatchCountByAlias,
          relationPageCountByAlias,
          relationRowCountByAlias,
          relationTruncatedByAlias,
        },
        durationMs: Math.round(performance.now() - startedAt),
        requestUrl: rootExecution.requestUrl,
        status: rootExecution.status,
        statusText: rootExecution.statusText,
        targetName: rootEntity.entitySetName,
      };
    },
  };
}
